import {API_BASE} from './reports';
import buildHeaders from './fetchHeaders';
import {getAwpApiBase} from '../awpApiBase';
import {getAwpClientHeaders} from '../../session/awpRequestHeaders';
import {isLinkedEntityRef, isLocalEntityRef} from '../entityLinks';

/**
 * Manually link a recognized-but-unlinked mention in a corpus segment to a Wikidata
 * or custom (D-) entity.
 * Wraps POST /api/entities/manual-link.
 *
 * Pass either {@code wikidataId} (Q…) or {@code localEntityRef} (D…), mutually exclusive.
 *
 * @param {Object} params
 * @param {number|string} params.segmentId
 * @param {string} params.mentionText - must match segment content at [startOffset, endOffset)
 * @param {number} params.startOffset - segment-relative start
 * @param {number} params.endOffset - segment-relative end
 * @param {string} [params.wikidataId] - Wikidata QID (e.g. "Q64")
 * @param {string} [params.localEntityRef] - custom entity ref (e.g. "D55577")
 * @param {string} [params.entityType] - optional NER type name
 * @param {string} [params.canonicalName] - preferred label when creating from Wikidata
 * @returns {Promise<Object>} backend response ({success, entity, mappingId, duplicate, ...})
 */
export async function linkSegmentEntity({
                                            segmentId,
                                            mentionText,
                                            startOffset,
                                            endOffset,
                                            wikidataId = null,
                                            localEntityRef = null,
                                            entityType = null,
                                            canonicalName = null,
                                        }) {
    const localRef = localEntityRef && isLocalEntityRef(String(localEntityRef).trim())
        ? String(localEntityRef).trim()
        : (wikidataId && isLocalEntityRef(String(wikidataId).trim())
            ? String(wikidataId).trim()
            : null);
    const qid = !localRef && wikidataId ? String(wikidataId).trim() : null;
    if (!localRef && !qid) {
        throw new Error('wikidataId or localEntityRef is required');
    }

    const body = {
        segmentId,
        mentionText,
        startOffset,
        endOffset,
        entityType,
        canonicalName,
    };
    if (localRef) {
        body.localEntityRef = localRef;
    } else {
        body.wikidataId = qid;
    }

    const response = await fetch(`${API_BASE}/entities/manual-link`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify(body),
    });

    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        // Non-JSON body; fall through to status-based error handling.
    }

    if (!response.ok || (payload && payload.success === false)) {
        const message = payload?.error || `Failed to link entity (status ${response.status})`;
        throw new Error(message);
    }

    return payload || {success: true};
}

/**
 * Cache of resolved {@code QID -> entityType} so repeat lookups (e.g. the same entity rendered
 * many times in a report) hit the network at most once per QID. {@code null} marks a QID that the
 * KB had no type for, so we don't re-request it.
 * @type {Map<string, string|null>}
 */
const typeCache = new Map();

/**
 * Batch-resolve the canonical NER/NEL entity type for a set of Wikidata QIDs from the knowledge
 * base ({@code entities.entity_type}) via POST /api/entities/types-by-qid. The type always reflects
 * what the NER/NEL pipeline stored — it is never inferred from Wikidata claims client-side.
 *
 * Already-cached QIDs are served from memory; only the uncached remainder is fetched. The returned
 * map merges cached + freshly fetched results and contains only QIDs that resolved to a type.
 *
 * @param {Array<string>} wikidataIds
 * @returns {Promise<Object<string, string>>} map of normalized QID -> entityType
 */
export async function fetchEntityTypesByQid(wikidataIds) {
    const normalized = [...new Set(
        (wikidataIds || [])
            .filter((q) => typeof q === 'string' && q.trim())
            .map((q) => q.trim().toUpperCase())
    )];
    if (normalized.length === 0) return {};

    const result = {};
    const missing = [];
    for (const qid of normalized) {
        if (typeCache.has(qid)) {
            const cached = typeCache.get(qid);
            if (cached != null) result[qid] = cached;
        } else {
            missing.push(qid);
        }
    }
    if (missing.length === 0) return result;

    try {
        const response = await fetch(`${API_BASE}/entities/types-by-qid`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }),
            body: JSON.stringify({wikidataIds: missing}),
        });
        if (response.ok) {
            const payload = await response.json();
            const types = (payload && payload.types) || {};
            for (const qid of missing) {
                const type = types[qid] || null;
                typeCache.set(qid, type);
                if (type != null) result[qid] = type;
            }
        }
    } catch (_) {
        // Best-effort: leave uncached QIDs unresolved; callers fall back to whatever they had.
    }
    return result;
}

/**
 * Normalized KB entity header fields from GET /api/entities/{entityRef}/summary.
 * @typedef {{ entityType: string|null, description: string|null, imageUrl: string|null, canonicalName: string|null }} KbEntitySummary
 */

/** @type {Map<string, KbEntitySummary|null>} */
const summaryCache = new Map();
/** @type {Map<string, Promise<KbEntitySummary|null>>} */
const summaryInflight = new Map();

function kbSummaryRequestUrl(entityRef) {
    const base = getAwpApiBase();
    return `${base}/api/entities/${encodeURIComponent(entityRef)}/summary`;
}

/**
 * Fetch authoritative KB header fields (type, description, avatar) for a linked entity ref.
 * Results are cached in-memory; concurrent requests for the same ref share one in-flight fetch.
 *
 * @param {string|null|undefined} entityRef - Wikidata QID or custom D/LOC ref
 * @returns {Promise<KbEntitySummary|null>}
 */
export async function fetchKbEntitySummary(entityRef) {
    if (!entityRef || !isLinkedEntityRef(entityRef)) return null;
    const key = entityRef.trim();
    if (summaryCache.has(key)) return summaryCache.get(key);
    if (summaryInflight.has(key)) return summaryInflight.get(key);

    const promise = (async () => {
        try {
            const response = await fetch(kbSummaryRequestUrl(key), {
                headers: getAwpClientHeaders(),
            });
            if (!response.ok) {
                summaryCache.set(key, null);
                return null;
            }
            const data = await response.json();
            const entityType = data?.entityType ? String(data.entityType).trim() : null;
            const normalized = {
                entityType: entityType || null,
                description: typeof data?.description === 'string' ? data.description.trim() : null,
                imageUrl: typeof data?.image === 'string' ? data.image.trim() : null,
                canonicalName: data?.canonicalName || data?.label || null,
            };
            summaryCache.set(key, normalized);
            if (entityType && key.match(/^Q\d+$/i)) {
                typeCache.set(key.toUpperCase(), entityType);
            }
            return normalized;
        } catch (_) {
            summaryCache.set(key, null);
            return null;
        } finally {
            summaryInflight.delete(key);
        }
    })();

    summaryInflight.set(key, promise);
    return promise;
}
